import { logger } from '@saaga/shared/utils/logger.util'

import { APP_BASE_PATH } from '@/app.const'

const STORAGE_KEY = 'saaga_deep_link_intent'
const ARRIVED_SESSION_KEY = 'saaga_deep_link_arrived'
const EXPIRY_MS = 30 * 60 * 1000

export interface DeepLinkIntent {
  agencySlug: string
  templateId?: string
  prefillValues?: Record<string, string>
  prompt?: string
  timestamp: number
}

interface StoredDeepLinkIntent {
  intent: DeepLinkIntent
  timestamp: number
}

export const saveDeepLinkIntent = (intent: DeepLinkIntent): void => {
  const stored: StoredDeepLinkIntent = { intent, timestamp: Date.now() }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored))
    logger.debug('[DeepLink] Intent saved', stored)
  } catch (error) {
    logger.warn('[DeepLink] Failed to save intent', error)
  }
}

export const hasDeepLinkIntent = (): boolean => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return false
    const stored: StoredDeepLinkIntent = JSON.parse(raw)
    return Date.now() - stored.timestamp <= EXPIRY_MS
  } catch {
    return false
  }
}

export const consumeDeepLinkIntent = (): DeepLinkIntent | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null

    localStorage.removeItem(STORAGE_KEY)

    const stored: StoredDeepLinkIntent = JSON.parse(raw)

    if (Date.now() - stored.timestamp > EXPIRY_MS) {
      logger.debug('[DeepLink] Intent expired, discarding')
      return null
    }

    logger.debug('[DeepLink] Intent consumed', stored.intent)
    return stored.intent
  } catch (error) {
    localStorage.removeItem(STORAGE_KEY)
    logger.warn('[DeepLink] Failed to read intent', error)
    return null
  }
}

export const markDeepLinkArrivedInSession = (): void => {
  try {
    sessionStorage.setItem(ARRIVED_SESSION_KEY, '1')
  } catch (error) {
    logger.warn('[DeepLink] Failed to mark arrival in session', error)
  }
}

export const hasDeepLinkArrivedInSession = (): boolean => {
  try {
    return sessionStorage.getItem(ARRIVED_SESSION_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * Extracts prefill values from URL search params.
 * All params except reserved ones (t, prompt) are treated as template variable prefills.
 */
const RESERVED_PARAMS = new Set(['t', 'prompt'])

export const extractPrefillValues = (searchParams: URLSearchParams): Record<string, string> => {
  const values: Record<string, string> = {}
  for (const [key, value] of searchParams.entries()) {
    if (!RESERVED_PARAMS.has(key) && value) {
      values[key] = value
    }
  }
  return values
}

export const buildDeepLinkChatUrl = (orgSlug: string, intent: DeepLinkIntent): string => {
  const base = `/${orgSlug}/chat/${intent.agencySlug}`
  const params = new URLSearchParams()

  if (intent.templateId) {
    params.set('t', intent.templateId)
  }
  if (intent.prompt) {
    params.set('prompt', intent.prompt)
  }
  if (intent.prefillValues) {
    for (const [key, value] of Object.entries(intent.prefillValues)) {
      params.set(key, value)
    }
  }

  const query = params.toString()
  return query ? `${base}?${query}` : base
}

interface BuildAgencyDeepLinkOptions {
  templateId?: string
  prefillValues?: Record<string, string>
}

const filterNonEmptyValues = (values: Record<string, string>): Record<string, string> =>
  Object.fromEntries(Object.entries(values).filter(([, value]) => value.length > 0))

export const buildAgencyDeepLink = (
  agencySlug: string,
  options: BuildAgencyDeepLinkOptions = {}
): string => {
  const origin = window.location.origin
  const base = `${origin}${APP_BASE_PATH}/deeplink/chat/${agencySlug}`
  const params = new URLSearchParams()

  if (options.templateId) {
    params.set('t', options.templateId)
  }
  if (options.prefillValues) {
    const nonEmpty = filterNonEmptyValues(options.prefillValues)
    for (const [key, value] of Object.entries(nonEmpty)) {
      params.set(key, value)
    }
  }

  const query = params.toString()
  return query ? `${base}?${query}` : base
}
